import axios from 'axios';
import { actions, mutations, state } from './types';
import { actions as notifActions } from '../notifications/types';

const POLL_INTERVAL_MS = 20000;

export default {
  async [actions.FETCH_CHANNELS]({ commit }, collectionId) {
    const { data } = await axios.get(
      `/api/collections/${collectionId}/channels`
    );
    if (data.success) {
      commit(mutations.SET_CHANNELS, data.channels);
      commit(mutations.SET_CHANNELS_FOR_COLLECTION, {
        collectionId,
        channels: data.channels,
      });
    }
  },

  async [actions.CREATE_CHANNEL](
    { dispatch },
    { collectionId, name, userIds }
  ) {
    const body = { name };
    if (userIds && userIds.length) body.user_ids = userIds;
    await axios.post(`/api/collections/${collectionId}/channels`, body);
    await dispatch(actions.FETCH_CHANNELS, collectionId);
  },

  async [actions.FIND_OR_CREATE_DM]({ dispatch }, { collectionId, userIds }) {
    const { data } = await axios.post(
      `/api/collections/${collectionId}/group_dms`,
      { user_ids: userIds }
    );
    await dispatch(actions.FETCH_CHANNELS, collectionId);
    return data.channel;
  },

  async [actions.ADD_CHANNEL_MEMBERS](
    { dispatch },
    { collectionId, channelName, userIds }
  ) {
    await axios.post(
      `/api/collections/${collectionId}/channels/${channelName}/members`,
      { user_ids: userIds }
    );
    await dispatch(actions.FETCH_CHANNELS, collectionId);
  },

  async [actions.LEAVE_CHANNEL]({ dispatch }, { collectionId, channelName }) {
    await axios.delete(
      `/api/collections/${collectionId}/channels/${channelName}/members`
    );
    await dispatch(actions.FETCH_CHANNELS, collectionId);
  },

  async [actions.FETCH_MEMBERS]({ commit }, collectionId) {
    try {
      const { data } = await axios.get(
        `/api/collections/${collectionId}/members`
      );
      if (data.success) {
        commit(mutations.SET_COLLECTION_MEMBERS, data.members);
        commit(mutations.MERGE_USERS, data.members);
      }
    } catch (_err) {
      // silently ignore
    }
  },

  [actions.ENTER_NEW_CONVERSATION]({ commit }) {
    commit(mutations.SET_NEW_CONVERSATION_MODE, true);
    commit(mutations.SET_ACTIVE_CHANNEL, null);
    commit(mutations.SET_MESSAGES, []);
    commit(mutations.SET_LAST_FETCHED_AT, null);
  },

  [actions.EXIT_NEW_CONVERSATION]({ commit }) {
    commit(mutations.SET_NEW_CONVERSATION_MODE, false);
  },

  [actions.SET_ACTIVE_CHANNEL](
    { commit, dispatch },
    { collectionId, channelName }
  ) {
    commit(mutations.SET_ACTIVE_COLLECTION_ID, collectionId);
    commit(mutations.SET_ACTIVE_CHANNEL, channelName);
    commit(mutations.SET_MESSAGES, []);
    commit(mutations.SET_LAST_FETCHED_AT, null);
    dispatch(actions.FETCH_MESSAGES);
  },

  async [actions.FETCH_MESSAGES]({ commit, dispatch, state: s }) {
    const collectionId = s[state.ACTIVE_COLLECTION_ID];
    const channelName = s[state.ACTIVE_CHANNEL];
    if (!collectionId || !channelName) return;

    const since = s[state.LAST_FETCHED_AT];
    const params = since ? { since } : {};

    try {
      const { data } = await axios.get(
        `/api/collections/${collectionId}/channels/${channelName}/messages`,
        { params }
      );
      if (data.success) {
        // disable time-based polling, see #1397
        //        const fetchedAt = new Date().toISOString();

        // ensure that requested collectionId and channelName match current collection and channel:
        // can't update messages unless this is the case
        if (
          channelName != s[state.ACTIVE_CHANNEL] ||
          collectionId != s[state.ACTIVE_COLLECTION_ID]
        ) {
          return;
        }

        if (since) {
          commit(mutations.APPEND_MESSAGES, data.messages);
          // New messages arrived during polling — keep the feed in sync immediately
          if (data.messages.length > 0) {
            dispatch(actions.FETCH_FEED);
          }
        } else {
          commit(mutations.SET_MESSAGES, data.messages);
        }
        // disable time-based polling, see #1397
        //        commit(mutations.SET_LAST_FETCHED_AT, fetchedAt);
      }
    } catch (_err) {
      // silently ignore poll errors
    }
  },

  async [actions.POST_MESSAGE](
    { dispatch },
    { collectionId, channelName, content, imageFile }
  ) {
    try {
      const url = `/api/collections/${collectionId}/channels/${channelName}/messages`;
      if (imageFile) {
        const formData = new FormData();
        formData.append('content', content || '');
        formData.append('image', imageFile);
        await axios.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axios.post(url, { content });
      }
    } catch (_err) {
      dispatch(
        'notifications/' + notifActions.NOTIFY_ERROR,
        'Failed to send message.',
        { root: true }
      );
    } finally {
      await dispatch(actions.FETCH_MESSAGES);
      dispatch(actions.FETCH_FEED);
    }
  },

  async [actions.EDIT_MESSAGE](
    { commit, dispatch },
    { collectionId, channelName, messageId, content }
  ) {
    try {
      await axios.post(
        `/api/collections/${collectionId}/channels/${channelName}/messages/${messageId}`,
        { content }
      );
    } catch (_err) {
      dispatch(
        'notifications/' + notifActions.NOTIFY_ERROR,
        'Failed to edit message.',
        { root: true }
      );
    } finally {
      // Reset since-timestamp so the full message list is re-fetched (edits only
      // change last_modified, not date_generated, so a since-based fetch misses them)
      commit(mutations.SET_LAST_FETCHED_AT, null);
      await dispatch(actions.FETCH_MESSAGES);
      dispatch(actions.FETCH_FEED);
    }
  },

  async [actions.DELETE_MESSAGE](
    { commit, dispatch },
    { collectionId, channelName, messageId }
  ) {
    try {
      await axios.delete(
        `/api/collections/${collectionId}/channels/${channelName}/messages/${messageId}`
      );
    } catch (_err) {
      dispatch(
        'notifications/' + notifActions.NOTIFY_ERROR,
        'Failed to delete message.',
        { root: true }
      );
    } finally {
      // Reset since-timestamp so the full message list is re-fetched and the
      // deleted message is removed from the store
      commit(mutations.SET_LAST_FETCHED_AT, null);
      await dispatch(actions.FETCH_MESSAGES);
      dispatch(actions.FETCH_FEED);
    }
  },

  [actions.START_POLLING]({ commit, dispatch, state: s }) {
    if (s[state.POLLING_INTERVAL_ID] !== null) return;
    const intervalId = setInterval(() => {
      dispatch(actions.FETCH_MESSAGES);
    }, POLL_INTERVAL_MS);
    commit(mutations.SET_POLLING_INTERVAL_ID, intervalId);
  },

  [actions.STOP_POLLING]({ commit, state: s }) {
    if (s[state.POLLING_INTERVAL_ID] !== null) {
      clearInterval(s[state.POLLING_INTERVAL_ID]);
      commit(mutations.SET_POLLING_INTERVAL_ID, null);
    }
  },

  async [actions.FETCH_FEED]({ commit, dispatch }) {
    try {
      const { data } = await axios.get('/api/user_feed');
      if (data.success) {
        commit(mutations.SET_FEED, data.activity_feed);
        if (data.channel_read_at) {
          commit(mutations.SET_CHANNEL_READ_AT, data.channel_read_at);
        }
        // Seed usersById from message authors (free — no extra API calls)
        commit(
          mutations.MERGE_USERS,
          data.activity_feed.map(m => ({ id: m.user_id, name: m.user }))
        );
        dispatch(actions.FETCH_FEED_CONTEXT);
      }
    } catch (_err) {
      // silently ignore
    }
  },

  async [actions.FETCH_FEED_CONTEXT]({ commit, state: s }) {
    const feed = s[state.FEED];
    const collectionIds = [
      ...new Set(feed.map(m => m.collection_id).filter(Boolean)),
    ];
    const cached = new Set(
      Object.keys(s[state.CHANNELS_BY_COLLECTION]).map(Number)
    );
    const toFetch = collectionIds.filter(id => !cached.has(id));
    await Promise.all(
      toFetch.map(async id => {
        try {
          const [chRes, memRes] = await Promise.all([
            axios.get(`/api/collections/${id}/channels`),
            axios.get(`/api/collections/${id}/members`),
          ]);
          if (chRes.data.success) {
            commit(mutations.SET_CHANNELS_FOR_COLLECTION, {
              collectionId: id,
              channels: chRes.data.channels,
            });
          }
          if (memRes.data.success) {
            commit(mutations.MERGE_USERS, memRes.data.members);
          }
        } catch (_err) {
          // ignore errors for individual collections
        }
      })
    );
  },

  async [actions.MARK_CHANNELS_READ]({ commit }, channelIds) {
    if (!channelIds.length) return;
    commit(mutations.MARK_CHANNELS_READ, channelIds);
    try {
      await axios.post('/api/user_feed/read', { channel_ids: channelIds });
    } catch (_err) {
      // silently ignore — optimistic update already applied
    }
  },

  [actions.MARK_CHANNEL_READ]({ dispatch, state: s }, { channelId }) {
    const inFeed = s[state.FEED].some(m => m.channel_id === channelId);
    if (inFeed) dispatch(actions.MARK_CHANNELS_READ, [channelId]);
  },

  [actions.MARK_ALL_READ]({ dispatch, state: s }) {
    const ids = [...new Set(s[state.FEED].map(m => m.channel_id))];
    if (ids.length) dispatch(actions.MARK_CHANNELS_READ, ids);
  },

  // Open the chat panel, optionally switching to a specific collection.
  // Channel loading is handled by ChatPanel's watcher on activeCollection.
  [actions.OPEN_CHAT]({ commit }, collection) {
    if (collection) {
      commit(mutations.SET_ACTIVE_COLLECTION, collection);
    }
    commit(mutations.SET_CHAT_PANEL_OPEN, true);
  },

  [actions.CLOSE_CHAT]({ commit, dispatch }) {
    dispatch(actions.STOP_POLLING);
    commit(mutations.SET_CHAT_PANEL_OPEN, false);
  },

  [actions.SET_CHAT_WIDTH]({ commit }, width) {
    commit(mutations.SET_CHAT_PANEL_WIDTH, width);
  },

  // Set active collection from a collection view. Channel loading (when
  // the panel is open) is handled by ChatPanel's watcher on activeCollection.
  [actions.SET_ACTIVE_COLLECTION]({ commit }, collection) {
    commit(mutations.SET_ACTIVE_COLLECTION, collection);
  },

  // Navigate the chat panel to a specific message (used by notification links).
  // Sets a scroll target that ChatPanel consumes after loading channels.
  [actions.NAVIGATE_TO_MESSAGE](
    { commit, dispatch },
    { collectionId, collectionLabel, channelName, messageId }
  ) {
    commit(mutations.SET_SCROLL_TARGET_MESSAGE_ID, {
      channelName,
      messageId,
    });
    const collection = { id: collectionId, label: collectionLabel };
    dispatch(actions.OPEN_CHAT, collection);
  },
};
